import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { isNodeSelection, posToDOMRect } from '@tiptap/core';
import { usePortalNode } from '@wix/da-ds/pkg/popper/hooks/usePortalNode';
import throttle from 'lodash/throttle';
import ddt from '@wix/da-ddt';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { getMenuUpdate } from '../../../redux/selectors/editor';
import s from './SelectionBubbleMenu.scss';
const ContainerPortal = ({ children })=>{
    const portalContainer = usePortalNode('bubblemenu');
    return portalContainer && /*#__PURE__*/ ReactDOM.createPortal(children, portalContainer);
};
/**
 * A very specific implementation of a bubblemenu, that floats near a selection range.
 *
 * Now for mobile this does not work at all because selecting text is hard on mobile
 * and mobile has native menus that get in the way. So in general don't use this for
 * mobile unless it to position a popover near a element in the editor.
 *
 * Use forceNode to pass in the element to use for the rect instead of the selection.
 *
 * Most implementations, including the tiptap one, use tooltip libraries to position
 * this stuff. However, to me that didn't work well. Those libs are designed to
 * show elements next to another element and IMO the problem inside editors is
 * actually different.
 *
 * For example, those impls. do not handle selecting text and then scrolling which is
 * a big deal when embedding images, you want the menu to follow the user and not force
 * then to scroll back up.
 *
 * As this bad UX always irked me with ricos and I couldn't ever fix it I decided to
 * just implement a very specific impl. for our uses.
 *
 * == Editor inside a scrollable
 *
 * For this usecase we require you to provide a ref of the scrollable area, we then
 * keep the menu inside the top and bottom of the scrollable.
 *
 * == Editor inside window
 *
 * For this usecase we will use viewport as the scrollable and keep the menu inside
 * the top of viewport.
 *
 * == Both
 *
 * For both we don't worry about off bottom of off left, and for right we will move
 * the menu back inside the viewport. We ignore the scrollable here too as we are
 * using portal we only check right viewport (I think this is good enough).
 *
 */ const SelectionBubbleMenu = ({ editor, open, className, children, scrollContainerRef, menuOffset = 8, menuHeight, menuWidth, borderPadding = 8, forceNode, forceBelowSelection = false, allowTopOverflow = true })=>{
    const menuUp = useSelector(getMenuUpdate); // used for externals to trigger updates
    const [winScrollPos, setWinScrollPos] = useState({
        y: window.scrollY,
        x: window.scrollX
    });
    const [innerScrollPos, setInnerScrollPos] = useState({
        y: 0,
        x: 0
    });
    const [menuStyles, setMenuStyles] = useState({});
    // Track the window position
    useEffect(()=>{
        const scroller = window;
        const handleScroll = throttle(()=>{
            setWinScrollPos({
                y: scroller === null || scroller === void 0 ? void 0 : scroller.scrollY,
                x: scroller === null || scroller === void 0 ? void 0 : scroller.scrollX
            });
        }, 200);
        if (scroller) {
            scroller.addEventListener('scroll', handleScroll, {
                passive: true
            });
        }
        return ()=>scroller === null || scroller === void 0 ? void 0 : scroller.removeEventListener('scroll', handleScroll);
    }, [
        open
    ]);
    // Force updates when a non page scroller scrolls, we need to update
    // the menu position on scroll / we don't actually use the value.
    useEffect(()=>{
        const scroller = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current;
        const handleScroll = throttle(()=>{
            setInnerScrollPos({
                y: scroller === null || scroller === void 0 ? void 0 : scroller.scrollTop,
                x: scroller === null || scroller === void 0 ? void 0 : scroller.scrollLeft
            });
        }, 200);
        if (scroller) {
            scroller.addEventListener('scroll', handleScroll, {
                passive: true
            });
        }
        return ()=>scroller === null || scroller === void 0 ? void 0 : scroller.removeEventListener('scroll', handleScroll);
    }, [
        scrollContainerRef,
        open
    ]);
    useEffect(()=>{
        if (open) {
            const { ranges } = editor.state.selection;
            const from = Math.min(...ranges.map((range)=>range.$from.pos));
            const to = Math.max(...ranges.map((range)=>range.$to.pos));
            if (!editor.view.docView) {
                return;
            }
            let rect;
            try {
                if (forceNode) {
                    // Node from external source
                    rect = forceNode.getBoundingClientRect();
                } else {
                    // Single node selected, rect from node (there is no selection)
                    if (isNodeSelection(editor.state.selection)) {
                        const node = editor.view.nodeDOM(from);
                        if (node instanceof HTMLElement) {
                            // Dont show on extensions
                            if (node === null || node === void 0 ? void 0 : node.classList.contains('react-renderer')) {
                                rect = undefined;
                            } else {
                                rect = node.getBoundingClientRect();
                            }
                        }
                    } else {
                        // Rect from selection rect
                        rect = posToDOMRect(editor.view, from, to);
                    }
                }
            } catch (e) {
                ddt.error('editor', e);
            }
            if (rect) {
                var _visualViewport, _scrollContainerRef_current;
                let newTop = rect.top + winScrollPos.y - menuOffset - menuHeight;
                if (forceBelowSelection) {
                    newTop = newTop + rect.height + menuHeight + menuOffset + 5;
                }
                let newLeft = rect.left + winScrollPos.x;
                let newRight = undefined;
                var _visualViewport_width;
                // off right of screen we move it back inside
                if (newLeft + menuWidth > ((_visualViewport_width = (_visualViewport = visualViewport) === null || _visualViewport === void 0 ? void 0 : _visualViewport.width) !== null && _visualViewport_width !== void 0 ? _visualViewport_width : Infinity)) {
                    newLeft = 'auto';
                    newRight = `${borderPadding}px`;
                }
                // off top of scrollarea
                // With ref we keep within the scrollable not the screen
                const scrollableRefRect = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : (_scrollContainerRef_current = scrollContainerRef.current) === null || _scrollContainerRef_current === void 0 ? void 0 : _scrollContainerRef_current.getBoundingClientRect();
                if (scrollableRefRect) {
                    // the selection rect top is outside the scrollarea and will be floating
                    // in the page, we bring it back into the scrollable area and account for
                    // viewport scroll pos.
                    if (!allowTopOverflow) {
                        if (rect.top - menuHeight < scrollableRefRect.top) {
                            newTop = scrollableRefRect.top + winScrollPos.y + borderPadding;
                        }
                    }
                    // Also keep the menu inside the bottom of the scrollable
                    if (newTop > scrollableRefRect.bottom + winScrollPos.y) {
                        newTop = scrollableRefRect.bottom - (menuHeight + borderPadding) + winScrollPos.y;
                    }
                }
                // Without ref, we keep within the screen
                if (!(scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current)) {
                    if (rect.top - menuHeight < 0) {
                        newTop = winScrollPos.y + borderPadding;
                    }
                }
                const calcStyles = {
                    top: `${newTop}px`,
                    left: `${newLeft}px`,
                    position: 'absolute',
                    right: newRight,
                    display: 'block'
                };
                ddt.log('editormenu', 'Menu Position', calcStyles, rect);
                setMenuStyles(calcStyles);
            /*
        Left here because it can be useful to visualise the rect :)
        const debugRect = document.getElementById('debug-rect');
        const debug = debugRect ?? document.createElement('div');
        if (!debugRect) {
          document.body.appendChild(debug);
        }

        const styles: React.CSSProperties = {
          position: 'absolute',
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          left: `${rect.left + window.scrollX}px`,
          top: `${rect.top + window.scrollY}px`,
        };

        debug.setAttribute('id', 'debug-rect');
        debug.style.position = 'absolute';
        debug.style.backgroundColor = '#ff0000aa';
        debug.style.width = `${rect.width}px`;
        debug.style.height = `${rect.height}px`;
        debug.style.left = styles.left as any;
        debug.style.top = styles.top as any;
        debug.style.zIndex = '999999';
        debug.style.minWidth = '10px';
        debug.style.pointerEvents = 'none';*/ } else {
                const calcStyles = {
                    display: 'none'
                };
                setMenuStyles(calcStyles);
            }
        }
    }, [
        editor.state.selection,
        editor.view,
        open,
        winScrollPos,
        innerScrollPos,
        menuOffset,
        menuHeight,
        scrollContainerRef,
        menuWidth,
        borderPadding,
        forceNode,
        forceBelowSelection,
        menuUp,
        allowTopOverflow
    ]);
    if (!open) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ContainerPortal, null, /*#__PURE__*/ React.createElement("div", {
        className: cn(s['wrapper'], s['fade-in'], className),
        style: menuStyles
    }, children));
};
SelectionBubbleMenu.displayName = 'SelectionBubbleMenu';
export default SelectionBubbleMenu;

//# sourceMappingURL=SelectionBubbleMenu.js.map